// ═══════════════════════════════════════════════════════════════════════════════
// Employer — pickup / drop-off task group wizard (create & edit)
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  RECURRENCE_TYPE,
  USER_ROLE,
  buildPickupDropoffAutoTitle,
  resolveTaskGroupTitleLocale,
  type PickupDropoffTaskConfig,
} from '@fdh/shared';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../components/ui/ToastProvider';
import { ApiClientError, taskGroupApi } from '../../lib/api';
import { isLimitReachedError } from '../../lib/limitError';
import { getHelperTodayYmd } from '../../lib/userTodayYmd';
import { mapApiClientErrorToMessage } from '../../i18n/mapApiClientError';
import { PersonNameSuggestionInput } from '../../components/employer/PersonNameSuggestionInput';
import {
  TaskGroupScheduleFields,
  type TaskGroupScheduleState,
} from '../../components/employer/TaskGroupScheduleFields';
import { AppButton } from '../../components/ui/AppButton';
import { AppModal } from '../../components/ui/AppModal';
import { TaskGroupWizardProgress } from '../../components/employer/TaskGroupWizardProgress';
import {
  InventoryWizardReviewStep,
} from '../../components/employer/InventoryWizardReviewStep';
import { buildPickupDropoffReviewRows } from '../../components/employer/taskGroupWizardReview';
import { WizardToggleTileButton } from '../../components/employer/WizardToggleTileButton';
import { NativeTimeInput } from '../../components/NativeTimeInput';

const TOTAL_STEPS = 7;
const LOCATION_SUGGESTION_KEYS = [
  'task.pickup.locationChip.school',
  'task.pickup.locationChip.tutoring',
  'task.pickup.locationChip.stadium',
  'task.pickup.locationChip.soccerPitch',
  'task.pickup.locationChip.mall',
] as const;

export const EmployerPickupDropoffTaskPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const { showSuccessToast, showErrorToast } = useToast();
  const navigate = useNavigate();
  const { groupId } = useParams<{ groupId?: string }>();
  const isEdit = Boolean(groupId);

  const [wizardStep, setWizardStep] = useState(1);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [pickupEnabled, setPickupEnabled] = useState(true);
  const [dropoffEnabled, setDropoffEnabled] = useState(true);
  const [personName, setPersonName] = useState('');
  const [location, setLocation] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [dropoffTime, setDropoffTime] = useState('');
  const [description, setDescription] = useState('');
  const [titlePickup, setTitlePickup] = useState('');
  const [titleDropoff, setTitleDropoff] = useState('');
  const [titleModalLeg, setTitleModalLeg] = useState<'pickup' | 'dropoff' | null>(null);
  const [titleModalDraft, setTitleModalDraft] = useState('');
  const [titleChoiceModalOpen, setTitleChoiceModalOpen] = useState(false);
  const [originalPersonName, setOriginalPersonName] = useState('');
  const [originalLocation, setOriginalLocation] = useState('');
  const [originalTitlePickup, setOriginalTitlePickup] = useState('');
  const [originalTitleDropoff, setOriginalTitleDropoff] = useState('');
  const [schedule, setSchedule] = useState<TaskGroupScheduleState>({
    recurrence: RECURRENCE_TYPE.NONE,
    recurrenceDays: [],
    monthlyKind: null,
    recurrenceMonthDays: [],
    recurrenceOrdinal: null,
    occurrenceDate: '',
    startDate: '',
    endDate: '',
    exclusionRanges: [],
    requirePhoto: true,
    notifyEmployer: true,
    remindHelperBeforeDue: false,
  });

  const todayYmd = useMemo(() => getHelperTodayYmd(user?.settings?.timezone), [user?.settings?.timezone]);

  const autoTitlePickup = useMemo(
    () =>
      buildPickupDropoffAutoTitle({
        locale: resolveTaskGroupTitleLocale(language),
        leg: 'pickup',
        personName,
        location,
      }),
    [language, personName, location],
  );
  const autoTitleDropoff = useMemo(
    () =>
      buildPickupDropoffAutoTitle({
        locale: resolveTaskGroupTitleLocale(language),
        leg: 'dropoff',
        personName,
        location,
      }),
    [language, personName, location],
  );

  const resolvedTitlePickup = titlePickup.trim() !== '' ? titlePickup.trim() : autoTitlePickup;
  const resolvedTitleDropoff = titleDropoff.trim() !== '' ? titleDropoff.trim() : autoTitleDropoff;
  const suggestedTitlePickup = autoTitlePickup;
  const suggestedTitleDropoff = autoTitleDropoff;

  const loadGroup = useCallback(async () => {
    if (!groupId) {
      return;
    }
    setLoading(true);
    try {
      const res = await taskGroupApi.get(groupId);
      if (!res.success || !res.data) {
        showErrorToast(t('task.pickup.loadError'));
        return;
      }
      const cfg = res.data.taskConfigs as PickupDropoffTaskConfig;
      setPersonName(cfg.personName);
      setOriginalPersonName(cfg.personName);
      setLocation(cfg.location);
      setOriginalLocation(cfg.location);
      setDescription(cfg.description);
      setPickupEnabled(cfg.pickup.enabled);
      setDropoffEnabled(cfg.dropoff.enabled);
      setPickupTime(cfg.pickup.time);
      setDropoffTime(cfg.dropoff.time);
      setTitlePickup(cfg.titleOverrides?.pickup ?? '');
      setTitleDropoff(cfg.titleOverrides?.dropoff ?? '');
      setSchedule({
        recurrence: cfg.recurrence,
        recurrenceDays: cfg.recurrenceDays ?? [],
        monthlyKind: cfg.monthlyKind ?? null,
        recurrenceMonthDays: cfg.recurrenceMonthDays ?? [],
        recurrenceOrdinal: cfg.recurrenceOrdinal ?? null,
        occurrenceDate: cfg.occurrenceDate,
        startDate: cfg.startDate,
        endDate: cfg.endDate,
        exclusionRanges: cfg.exclusionRanges,
        requirePhoto: cfg.requirePhoto,
        notifyEmployer: cfg.notifyEmployer,
        remindHelperBeforeDue: cfg.remindHelperBeforeDue,
      });
      const cfgAutoPickup = buildPickupDropoffAutoTitle({
        locale: resolveTaskGroupTitleLocale(language),
        leg: 'pickup',
        personName: cfg.personName,
        location: cfg.location,
      });
      const cfgAutoDropoff = buildPickupDropoffAutoTitle({
        locale: resolveTaskGroupTitleLocale(language),
        leg: 'dropoff',
        personName: cfg.personName,
        location: cfg.location,
      });
      const activePickup = res.data.tasks.find((row) => row.dueTime === cfg.pickup.time);
      const activeDropoff = res.data.tasks.find((row) => row.dueTime === cfg.dropoff.time);
      setOriginalTitlePickup(activePickup?.title ?? cfgAutoPickup);
      setOriginalTitleDropoff(activeDropoff?.title ?? cfgAutoDropoff);
    } catch {
      showErrorToast(t('task.pickup.loadError'));
    } finally {
      setLoading(false);
    }
  }, [groupId, language, showErrorToast, t]);

  useEffect(() => {
    if (isEdit) {
      void loadGroup();
    }
  }, [isEdit, loadGroup]);

  useEffect(() => {
    if (schedule.occurrenceDate === '' && todayYmd !== '' && !isEdit) {
      setSchedule((prev) => ({ ...prev, occurrenceDate: todayYmd, startDate: todayYmd }));
    }
  }, [isEdit, schedule.occurrenceDate, todayYmd]);

  useEffect(() => {
    if (!titleModalLeg) {
      return;
    }
    const stored = titleModalLeg === 'pickup' ? titlePickup : titleDropoff;
    const auto = titleModalLeg === 'pickup' ? autoTitlePickup : autoTitleDropoff;
    setTitleModalDraft(stored.trim() !== '' ? stored : auto);
  }, [titleModalLeg, titlePickup, titleDropoff, autoTitlePickup, autoTitleDropoff]);

  const pickupReviewRows = useMemo(() => {
    const rows = buildPickupDropoffReviewRows({
      t,
      personName,
      location,
      description,
      pickupEnabled,
      dropoffEnabled,
      resolvedTitlePickup,
      resolvedTitleDropoff,
      pickupTime,
      dropoffTime,
      schedule,
    });
    return rows.map((row) => {
      if (row.id === 'rev-pickup-title') {
        return {
          ...row,
          onEdit: () => setTitleModalLeg('pickup'),
          editAriaLabel: t('task.pickup.editTitleAria'),
        };
      }
      if (row.id === 'rev-dropoff-title') {
        return {
          ...row,
          onEdit: () => setTitleModalLeg('dropoff'),
          editAriaLabel: t('task.pickup.editTitleAria'),
        };
      }
      return row;
    });
  }, [
    t,
    personName,
    location,
    description,
    pickupEnabled,
    dropoffEnabled,
    resolvedTitlePickup,
    resolvedTitleDropoff,
    pickupTime,
    dropoffTime,
    schedule,
  ]);

  if (!user || user.role !== USER_ROLE.EMPLOYER) {
    return (
      <div className="flex min-h-full items-center justify-center px-4">
        <p role="alert">{t('task.inventory.employerOnly')}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
      </div>
    );
  }

  const validateStep = (): string | null => {
    if (wizardStep === 1 && !pickupEnabled && !dropoffEnabled) {
      return t('task.pickup.legRequired');
    }
    if (wizardStep === 2 && personName.trim() === '') {
      return t('task.pickup.personRequired');
    }
    if (wizardStep === 3) {
      if (dropoffEnabled && dropoffTime.trim() === '') {
        return t('task.pickup.dropoffTimeRequired');
      }
      if (pickupEnabled && pickupTime.trim() === '') {
        return t('task.pickup.pickupTimeRequired');
      }
      if (pickupEnabled && dropoffEnabled && pickupTime.trim() === dropoffTime.trim()) {
        return t('task.pickup.timesMustDiffer');
      }
    }
    if (wizardStep === 6) {
      if (schedule.recurrence === RECURRENCE_TYPE.NONE && schedule.occurrenceDate === '') {
        return t('task.inventory.dueDateInvalid');
      }
      if (schedule.recurrence !== RECURRENCE_TYPE.NONE && schedule.startDate === '') {
        return t('task.form.startDateLabel');
      }
      if (
        (schedule.recurrence === RECURRENCE_TYPE.WEEKLY ||
          schedule.recurrence === RECURRENCE_TYPE.BIWEEKLY) &&
        schedule.recurrenceDays.length === 0
      ) {
        return t('task.form.recurrenceWeekdaysRequired');
      }
      if (schedule.recurrence === RECURRENCE_TYPE.MONTHLY) {
        if (schedule.monthlyKind === 'day' && schedule.recurrenceMonthDays.length === 0) {
          return t('task.form.recurrenceMonthDaysRequired');
        }
        if (
          (schedule.monthlyKind === 'nth_weekday' || schedule.monthlyKind === 'last_weekday') &&
          schedule.recurrenceDays.length === 0
        ) {
          return t('task.form.recurrenceWeekdaysRequired');
        }
      }
    }
    return null;
  };

  const shouldShowTitleChoice =
    isEdit &&
    (personName.trim() !== originalPersonName.trim() || location.trim() !== originalLocation.trim());

  const goReview = () => {
    if (shouldShowTitleChoice) {
      setTitleChoiceModalOpen(true);
      return;
    }
    setWizardStep(7);
  };

  const handleNext = () => {
    const err = validateStep();
    if (err) {
      showErrorToast(err);
      return;
    }
    if (wizardStep === 6) {
      goReview();
      return;
    }
    setWizardStep((s) => Math.min(TOTAL_STEPS, s + 1));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        personName: personName.trim(),
        location: location.trim(),
        description: description.trim(),
        pickupEnabled,
        dropoffEnabled,
        pickupTime: pickupEnabled ? pickupTime : '',
        dropoffTime: dropoffEnabled ? dropoffTime : '',
        titleOverridePickup: titlePickup.trim() !== '' ? titlePickup.trim() : undefined,
        titleOverrideDropoff: titleDropoff.trim() !== '' ? titleDropoff.trim() : undefined,
        locale: resolveTaskGroupTitleLocale(language),
        ...schedule,
      };
      const res = isEdit && groupId
        ? await taskGroupApi.updatePickupDropoff(groupId, payload)
        : await taskGroupApi.createPickupDropoff(payload);
      if (res.success) {
        showSuccessToast(isEdit ? t('task.pickup.updateSuccess') : t('task.pickup.createSuccess'));
        navigate('/tasks', { replace: true });
        return;
      }
      showErrorToast(res.error ?? t('task.pickup.createError'));
    } catch (caught) {
      if (isLimitReachedError(caught)) {
        return;
      }
      const msg =
        caught instanceof ApiClientError
          ? mapApiClientErrorToMessage(caught, t) ?? caught.message
          : t('task.pickup.createError');
      showErrorToast(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTitleModalConfirm = () => {
    const trimmed = titleModalDraft.trim();
    if (titleModalLeg === 'pickup') {
      setTitlePickup(trimmed);
    } else if (titleModalLeg === 'dropoff') {
      setTitleDropoff(trimmed);
    }
    setTitleModalLeg(null);
    // Ensure we always return to the Review step after editing titles.
    setWizardStep(7);
  };

  const handleTitleModalCancel = () => {
    setTitleModalLeg(null);
    // Closing the Custom input should still land back on Review.
    setWizardStep(7);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleBackKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleBack();
    }
  };

  return (
    <div className="min-h-full bg-gray-50 pb-24 dark:bg-gray-950">
      <header className="z-10 border-b border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <div className="mx-auto flex max-w-lg items-center gap-3 px-4 py-4">
          <button
            type="button"
            onClick={handleBack}
            onKeyDown={handleBackKeyDown}
            tabIndex={0}
            className="flex h-10 w-10 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-300 dark:hover:bg-gray-800"
            aria-label={t('task.action.back')}
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100">
            {isEdit ? t('task.pickup.editHeading') : t('task.pickup.createHeading')}
          </h1>
        </div>
      </header>
      <main className="mx-auto max-w-lg space-y-4 px-4 py-6">
        <TaskGroupWizardProgress currentStep={wizardStep} totalSteps={TOTAL_STEPS} t={t} />
        {wizardStep < 7 ? (
        <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          {wizardStep === 1 ? (
            <div>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {t('task.pickup.legHint')}
              </span>
              <div className="mt-2 flex gap-2" role="group" aria-label={t('task.pickup.legHint')}>
                <WizardToggleTileButton
                  selected={dropoffEnabled}
                  label={t('task.pickup.dropoffLabel')}
                  onToggle={() => setDropoffEnabled((prev) => !prev)}
                  ariaLabel={t('task.pickup.dropoffLabel')}
                />
                <WizardToggleTileButton
                  selected={pickupEnabled}
                  label={t('task.pickup.pickupLabel')}
                  onToggle={() => setPickupEnabled((prev) => !prev)}
                  ariaLabel={t('task.pickup.pickupLabel')}
                />
              </div>
            </div>
          ) : null}
          {wizardStep === 2 ? (
            <PersonNameSuggestionInput
              id="pickup-person"
              label={t('task.pickup.personLabel')}
              value={personName}
              onChange={setPersonName}
              placeholder={t('task.pickup.personPlaceholder')}
              suggestHeading={t('task.pickup.suggestHeading')}
            />
          ) : null}
          {wizardStep === 3 ? (
            <div className="space-y-4">
              {dropoffEnabled ? (
                <div>
                  <label className="text-sm font-medium" htmlFor="dropoff-time">{t('task.pickup.dropoffTimeLabel')}</label>
                  <NativeTimeInput id="dropoff-time" value={dropoffTime} onChange={(e) => setDropoffTime(e.target.value)} wrapperClassName="mt-1 w-full" className="min-h-11 w-full rounded-lg border border-gray-300 px-3 dark:border-gray-600 dark:bg-gray-800" />
                </div>
              ) : null}
              {pickupEnabled ? (
                <div>
                  <label className="text-sm font-medium" htmlFor="pickup-time">{t('task.pickup.pickupTimeLabel')}</label>
                  <NativeTimeInput id="pickup-time" value={pickupTime} onChange={(e) => setPickupTime(e.target.value)} wrapperClassName="mt-1 w-full" className="min-h-11 w-full rounded-lg border border-gray-300 px-3 dark:border-gray-600 dark:bg-gray-800" />
                </div>
              ) : null}
            </div>
          ) : null}
          {wizardStep === 4 ? (
            <div>
              <label className="text-sm font-medium" htmlFor="pickup-location">{t('task.pickup.locationLabel')}</label>
              <input id="pickup-location" value={location} onChange={(e) => setLocation(e.target.value)} className="mt-1 w-full min-h-11 rounded-lg border border-gray-300 px-3 dark:border-gray-600 dark:bg-gray-800" maxLength={150} />
              <div className="mt-3">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('task.pickup.locationSuggestHeading')}</p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {LOCATION_SUGGESTION_KEYS.map((labelKey) => (
                    <button
                      key={labelKey}
                      type="button"
                      onClick={() => setLocation(t(labelKey))}
                      className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
                    >
                      {t(labelKey)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
          {wizardStep === 5 ? (
            <div>
              <label className="text-sm font-medium" htmlFor="pickup-desc">{t('task.form.descLabelOptional')}</label>
              <textarea id="pickup-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('task.form.descPlaceholder')} rows={3} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-800" maxLength={1000} />
            </div>
          ) : null}
          {wizardStep === 6 ? (
            <TaskGroupScheduleFields t={t} schedule={schedule} onScheduleChange={(p) => setSchedule((s) => ({ ...s, ...p }))} todayYmd={todayYmd} isEdit={isEdit} />
          ) : null}
        </div>
        ) : null}
        {wizardStep === 7 ? (
          <div className="space-y-3">
            <InventoryWizardReviewStep
              cardHeading={t('task.group.wizard.reviewCardHeadingPickup')}
              rows={pickupReviewRows}
              backLabel={t('common.back')}
              primaryLabel={
                isEdit ? t('task.group.wizard.reviewUpdatePickup') : t('task.group.wizard.reviewConfirmPickup')
              }
              primaryBusyLabel={t('common.loading')}
              isPrimaryBusy={submitting}
              isBackDisabled={submitting}
              isPrimaryDisabled={submitting}
              onBack={() => setWizardStep(6)}
              onPrimary={() => void handleSubmit()}
            />
          </div>
        ) : null}
        {wizardStep < 7 ? (
        <div className="flex gap-3">
          {wizardStep > 1 ? (
            <AppButton type="button" variant="secondary" onClick={() => setWizardStep((s) => s - 1)} className="flex-1">
              {t('common.back')}
            </AppButton>
          ) : null}
          <AppButton type="button" variant="primary" onClick={handleNext} className="flex-1">
            {t('common.next')}
          </AppButton>
        </div>
        ) : null}
      </main>

      <AppModal
        open={Boolean(titleModalLeg)}
        onClose={handleTitleModalCancel}
        title={t('task.pickup.customTitleModalTitle')}
        closeAriaLabel={t('common.cancel')}
        panelMaxWidthClass="max-w-sm"
      >
        <input
          value={titleModalDraft}
          onChange={(e) => setTitleModalDraft(e.target.value)}
          className="mt-1 w-full min-h-11 rounded-lg border border-gray-300 px-3 dark:border-gray-600 dark:bg-gray-800"
          maxLength={100}
        />
        <div className="mt-4 flex gap-3">
          <AppButton type="button" variant="secondary" onClick={handleTitleModalCancel} className="flex-1">
            {t('common.cancel')}
          </AppButton>
          <AppButton type="button" variant="primary" onClick={handleTitleModalConfirm} className="flex-1">
            {t('common.confirm')}
          </AppButton>
        </div>
      </AppModal>

      <AppModal
        open={titleChoiceModalOpen}
        onClose={() => setTitleChoiceModalOpen(false)}
        title={t('task.pickup.titleChoiceTitle')}
        closeAriaLabel={t('common.cancel')}
      >
        {dropoffEnabled ? (
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {t('task.pickup.titleChoiceDropoff')}: {originalTitleDropoff} {'→ '}
            <span className="font-bold text-primary-700 dark:text-primary-300">{suggestedTitleDropoff}</span>
          </p>
        ) : null}
        {pickupEnabled ? (
          <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
            {t('task.pickup.titleChoicePickup')}: {originalTitlePickup} {'→ '}
            <span className="font-bold text-primary-700 dark:text-primary-300">{suggestedTitlePickup}</span>
          </p>
        ) : null}
        <div className="mt-4 flex flex-col gap-2">
          <AppButton
            type="button"
            variant="secondary"
            fullWidth
            onClick={() => {
              setTitlePickup(originalTitlePickup);
              setTitleDropoff(originalTitleDropoff);
              setTitleChoiceModalOpen(false);
              setWizardStep(7);
            }}
          >
            {t('task.pickup.titleChoiceKeep')}
          </AppButton>
          <AppButton
            type="button"
            variant="primary"
            fullWidth
            onClick={() => {
              setTitlePickup('');
              setTitleDropoff('');
              setTitleChoiceModalOpen(false);
              setWizardStep(7);
            }}
          >
            {t('task.pickup.titleChoiceUseNew')}
          </AppButton>
        </div>
      </AppModal>
    </div>
  );
};
